import { useEffect, useState, type ReactNode } from "react";
import {
  api,
  ApiError,
  type ApiKeySummary,
  type MeResponse,
  type OrgListItem,
  type OrgWithRole,
  type MemberSummary,
} from "../api";
import { Tag } from "../components/design/Tag";
import { Icon, type IconName } from "../components/design/Icon";
import { navigate, routes, type SettingsSection } from "../router";

const SECTIONS: ReadonlyArray<{
  id: SettingsSection;
  label: string;
  icon: IconName;
}> = [
  { id: "account", label: "Account", icon: "user" },
  { id: "organization", label: "Organization", icon: "user" },
  { id: "integrations", label: "Integrations", icon: "link" },
  { id: "api-keys", label: "API keys", icon: "key" },
  { id: "defaults", label: "Meeting defaults", icon: "cog" },
  { id: "billing", label: "Billing & plan", icon: "spark" },
];

export function Settings({ section }: { section: SettingsSection }) {
  return (
    <div
      className="recspec-settings-body"
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <aside
        className="recspec-settings-nav"
        style={{
          borderRight: "1px solid var(--rule)",
          background: "var(--paper-2)",
          padding: "20px 12px",
          overflow: "auto",
        }}
      >
        <div className="eyebrow" style={{ padding: "4px 8px 6px" }}>
          Settings
        </div>
        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {SECTIONS.map((s) => {
            const active = s.id === section;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => navigate(routes.settings(s.id))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: 0,
                  background: active ? "var(--card)" : "transparent",
                  cursor: "pointer",
                  boxShadow: active ? "var(--sh-1)" : "none",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? "var(--ink)" : "var(--ink-2)",
                  flexShrink: 0,
                }}
              >
                <Icon
                  name={s.icon}
                  size={14}
                  color={active ? "var(--ink)" : "var(--ink-3)"}
                />
                {s.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <main
        className="recspec-settings-content"
        style={{
          padding: "32px 40px",
          overflow: "auto",
          background: "var(--paper)",
        }}
      >
        <div style={{ maxWidth: 720 }}>
          {section === "account" && <AccountSection />}
          {section === "organization" && <OrganizationSection />}
          {section === "integrations" && <IntegrationsSection />}
          {section === "api-keys" && <ApiKeysSection />}
          {section === "defaults" && <DefaultsSection />}
          {section === "billing" && <BillingSection />}
        </div>
      </main>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <header style={{ marginBottom: 24 }}>
      <h1
        className="serif"
        style={{
          margin: 0,
          fontSize: 32,
          fontWeight: 400,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p className="dim" style={{ marginTop: 6, fontSize: 14 }}>
          {subtitle}
        </p>
      )}
    </header>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      className="recspec-settings-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 0",
        borderBottom: "1px solid var(--rule)",
      }}
    >
      <div
        style={{
          width: 160,
          flexShrink: 0,
          fontSize: 13,
          color: "var(--ink-3)",
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

function AccountSection() {
  const [me, setMe] = useState<MeResponse | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((r) => alive && setMe(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!me) return <div className="dim">Loading…</div>;
  const provider =
    me.auth_method === "cookie"
      ? "GitHub"
      : me.auth_method === "bearer_api_key"
        ? "API key"
        : me.identity.email
          ? "Email"
          : "Bearer";
  const display =
    me.auth_method === "cookie"
      ? `@${me.identity.gh_login}`
      : me.auth_method === "bearer_api_key"
        ? me.identity.uid
        : me.identity.email;
  const email =
    me.auth_method === "cookie"
      ? me.identity.email
      : me.auth_method === "bearer_api_key"
        ? undefined
        : me.identity.email;
  return (
    <>
      <SectionHeader
        title="Account"
        subtitle="Identity and session controls."
      />
      <Row label="Display name">
        <span style={{ fontWeight: 500 }}>{display}</span>
      </Row>
      <Row label="Email">
        <span className="mono" style={{ fontSize: 13 }}>
          {email ?? "—"}
        </span>
      </Row>
      <Row label="Auth provider">
        <Tag>{provider}</Tag>
      </Row>
      <Row label="Sign out">
        <button
          type="button"
          className="btn"
          onClick={async () => {
            await api.logout();
            navigate("/login");
          }}
        >
          Sign out everywhere
        </button>
      </Row>
    </>
  );
}

type IntegrationStatus = "connected" | "not_connected" | "action_needed";

const INTEGRATIONS: ReadonlyArray<{
  id: string;
  name: string;
  description: string;
  status: IntegrationStatus;
  /** When false, the action button is disabled with a "coming soon" tooltip. */
  ready: boolean;
}> = [
  {
    id: "google_meet",
    name: "Google Meet",
    description: "The bot joins your Meets to capture audio + screen-share.",
    status: "connected",
    ready: false,
  },
  {
    id: "google_calendar",
    name: "Google Calendar",
    description: "Create Meets directly from the picker (start-with-bot path).",
    status: "not_connected",
    ready: false,
  },
  {
    id: "slack",
    name: "Slack",
    description: "Post finalize bundles into a channel + trigger meetings via /recspec.",
    status: "not_connected",
    ready: false,
  },
  {
    id: "github",
    name: "GitHub",
    description: "Open OpenSpec change PRs with the spec-author bundle.",
    status: "connected",
    ready: false,
  },
  {
    id: "linear",
    name: "Linear",
    description: "Convert risks + open questions into Linear issues at finalize.",
    status: "not_connected",
    ready: false,
  },
  {
    id: "drive",
    name: "Drive",
    description: "Mirror finalize bundles to a Drive folder.",
    status: "not_connected",
    ready: false,
  },
];

function IntegrationsSection() {
  return (
    <>
      <SectionHeader
        title="Integrations"
        subtitle="Connect the tools the bot writes back to. Each integration ships in its own change; until then the buttons here are disabled placeholders."
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {INTEGRATIONS.map((i) => (
          <div
            key={i.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "16px 0",
              borderBottom: "1px solid var(--rule)",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 14 }}>{i.name}</strong>
                <StatusBadge status={i.status} />
              </div>
              <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
                {i.description}
              </div>
            </div>
            <button
              type="button"
              className="btn sm"
              disabled={!i.ready}
              title={
                !i.ready
                  ? "Coming soon — the integration ships in its own change."
                  : undefined
              }
              style={{
                opacity: i.ready ? 1 : 0.55,
                cursor: i.ready ? "pointer" : "not-allowed",
              }}
            >
              {i.status === "connected" ? "Reconfigure" : "Connect"}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: IntegrationStatus }) {
  if (status === "connected") {
    return <Tag color="ok">Connected</Tag>;
  }
  if (status === "action_needed") {
    return <Tag color="warn">Action needed</Tag>;
  }
  return <Tag>Not connected</Tag>;
}

// ── Organization ──────────────────────────────────────────────────

function OrganizationSection() {
  const [orgs, setOrgs] = useState<OrgListItem[] | null>(null);
  const [activeOrg, setActiveOrg] = useState<OrgWithRole | null>(null);
  const [members, setMembers] = useState<MemberSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.orgs.list();
        if (!alive) return;
        setOrgs(list.orgs);
        // Default-render the user's first org (most users have exactly
        // one in v1). The multi-org switcher is a follow-up.
        if (list.orgs.length > 0) {
          const first = list.orgs[0];
          const [detail, memberList] = await Promise.all([
            api.orgs.get(first.org_id),
            api.orgs.members.list(first.org_id),
          ]);
          if (!alive) return;
          setActiveOrg(detail);
          setMembers(memberList.members);
        }
      } catch (e) {
        if (!alive) return;
        setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (error) {
    return (
      <>
        <SectionHeader title="Organization" />
        <div className="dim" style={{ color: "var(--danger)" }}>
          {error}
        </div>
      </>
    );
  }
  if (orgs === null) return <div className="dim">Loading…</div>;
  if (orgs.length === 0) {
    return (
      <>
        <SectionHeader
          title="Organization"
          subtitle="You aren't a member of any organization yet."
        />
        <p className="dim">
          On a fresh sign-in, a personal organization is created
          automatically. If you're seeing this state, contact support.
        </p>
      </>
    );
  }
  if (activeOrg === null) return <div className="dim">Loading…</div>;

  const canEditBilling = activeOrg.role === "owner";

  return (
    <>
      <SectionHeader
        title="Organization"
        subtitle={`You are ${activeOrg.role} of ${activeOrg.name}.`}
      />

      <SettingsRow label="Name">
        <code style={{ fontSize: 14 }}>{activeOrg.name}</code>
      </SettingsRow>
      <SettingsRow label="Slug">
        <code style={{ fontSize: 14 }}>{activeOrg.slug}</code>
        <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
          The Recall.ai bot's display name in Google Meet uses this slug
          (e.g. <code>recspec for {activeOrg.slug}</code>). Slug rename
          is not supported in v1.
        </div>
      </SettingsRow>
      <SettingsRow label="Status">
        <Tag color={activeOrg.status === "active" ? "ok" : "warn"}>
          {activeOrg.status}
        </Tag>
      </SettingsRow>

      <h2
        className="serif"
        style={{ marginTop: 32, fontSize: 20, fontWeight: 400 }}
      >
        Brazilian billing
      </h2>
      <p className="dim" style={{ marginTop: 6, fontSize: 13 }}>
        These fields land on every NFS-e issued for this organization.
        Editable by Owner only. Unlocks fully when the billing adapter
        ships.
      </p>
      <SettingsRow label="CNPJ">
        <code style={{ fontSize: 14 }}>{activeOrg.cnpj ?? "—"}</code>
      </SettingsRow>
      <SettingsRow label="Razão social">
        <code style={{ fontSize: 14 }}>
          {activeOrg.razao_social ?? "—"}
        </code>
      </SettingsRow>
      <SettingsRow label="Código de serviço (ISS)">
        <code style={{ fontSize: 14 }}>
          {activeOrg.codigo_servico_iss ?? "—"}
        </code>
      </SettingsRow>
      <SettingsRow label="Inscrição municipal">
        <code style={{ fontSize: 14 }}>
          {activeOrg.inscricao_municipal ?? "—"}
        </code>
      </SettingsRow>
      {!canEditBilling && (
        <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
          Owner permissions are required to edit billing fields.
        </div>
      )}

      <h2
        className="serif"
        style={{ marginTop: 32, fontSize: 20, fontWeight: 400 }}
      >
        Members
      </h2>
      <p className="dim" style={{ marginTop: 6, fontSize: 13 }}>
        {members?.length ?? 0} member{(members?.length ?? 0) === 1 ? "" : "s"}.
      </p>
      <table
        className="recspec-api-keys-table"
        style={{ width: "100%", marginTop: 12, fontSize: 13 }}
      >
        <thead>
          <tr style={{ textAlign: "left", color: "var(--ink-2)" }}>
            <th style={{ padding: "8px 12px" }}>User ID</th>
            <th style={{ padding: "8px 12px" }}>Role</th>
            <th style={{ padding: "8px 12px" }}>Joined</th>
          </tr>
        </thead>
        <tbody>
          {(members ?? []).map((m) => (
            <tr key={m.user_id} style={{ borderTop: "1px solid var(--rule)" }}>
              <td style={{ padding: "8px 12px" }}>
                <code style={{ fontSize: 12 }}>{m.user_id}</code>
              </td>
              <td style={{ padding: "8px 12px" }}>
                <Tag color={m.role === "owner" ? "ok" : undefined}>
                  {m.role}
                </Tag>
              </td>
              <td style={{ padding: "8px 12px", color: "var(--ink-3)" }}>
                {new Date(m.joined_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {orgs.length > 1 && (
        <>
          <h2
            className="serif"
            style={{ marginTop: 32, fontSize: 20, fontWeight: 400 }}
          >
            Switch organization
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {orgs.map((o) => (
              <button
                key={o.org_id}
                type="button"
                onClick={async () => {
                  await api.orgs.activate(o.org_id);
                  window.location.reload();
                }}
                disabled={o.org_id === activeOrg.org_id}
                style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--rule)",
                  background:
                    o.org_id === activeOrg.org_id
                      ? "var(--card)"
                      : "var(--paper)",
                  cursor:
                    o.org_id === activeOrg.org_id ? "default" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                <div style={{ fontWeight: 500 }}>{o.name}</div>
                <div className="dim" style={{ fontSize: 12 }}>
                  slug: <code>{o.slug}</code> · role: {o.role}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function SettingsRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "200px 1fr",
        alignItems: "start",
        padding: "10px 0",
        borderBottom: "1px solid var(--rule)",
      }}
    >
      <div className="dim" style={{ fontSize: 13 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);

  const refresh = async () => {
    try {
      const r = await api.apiKeys.list();
      setKeys(r.keys);
      setLoadError(null);
    } catch (e) {
      if (e instanceof ApiError) setLoadError(e.message);
      else setLoadError(String(e));
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <>
      <SectionHeader
        title="API keys"
        subtitle="Bearer tokens for MCP clients and other integrations. Each key carries the same authority as your account, so revoke any you don't recognize."
      />

      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreate(true)}
        >
          Create new key
        </button>
      </div>

      {loadError && (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            border: "1px solid var(--rule)",
            background: "var(--paper-2)",
            color: "var(--ink-2)",
            fontSize: 13,
          }}
        >
          Couldn't load keys: {loadError}
        </div>
      )}

      {keys === null && !loadError && (
        <div className="dim" style={{ fontSize: 13 }}>
          Loading…
        </div>
      )}

      {keys !== null && keys.length === 0 && (
        <div
          className="dim"
          style={{
            padding: "24px 0",
            borderBottom: "1px solid var(--rule)",
            fontSize: 13,
          }}
        >
          No keys yet. Click "Create new key" to mint your first one.
        </div>
      )}

      {keys !== null && keys.length > 0 && (
        <ApiKeysTable
          keys={keys}
          onRevoke={(rec) => setRevokeTarget(rec)}
        />
      )}

      {showCreate && (
        <CreateApiKeyModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            refresh();
          }}
        />
      )}

      {revokeTarget && (
        <RevokeApiKeyModal
          target={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={() => {
            setRevokeTarget(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function ApiKeysTable({
  keys,
  onRevoke,
}: {
  keys: ApiKeySummary[];
  onRevoke: (rec: ApiKeySummary) => void;
}) {
  return (
    <div
      className="recspec-api-keys-table"
      role="table"
      aria-label="API keys"
      style={{
        borderTop: "1px solid var(--rule)",
        fontSize: 13,
      }}
    >
      <div
        role="row"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 110px 130px 130px 110px 90px",
          gap: 12,
          padding: "10px 12px",
          color: "var(--ink-3)",
          fontWeight: 500,
          borderBottom: "1px solid var(--rule)",
          background: "var(--paper-2)",
        }}
      >
        <div role="columnheader">Name</div>
        <div role="columnheader">Prefix</div>
        <div role="columnheader">Created</div>
        <div role="columnheader">Last used</div>
        <div role="columnheader">Status</div>
        <div role="columnheader" style={{ textAlign: "right" }}>
          Actions
        </div>
      </div>
      {keys.map((k) => (
        <div
          key={k.key_id}
          role="row"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 110px 130px 130px 110px 90px",
            gap: 12,
            padding: "12px",
            alignItems: "center",
            borderBottom: "1px solid var(--rule)",
          }}
        >
          <div role="cell" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {k.name}
          </div>
          <div role="cell" className="mono" style={{ fontSize: 12 }}>
            rsk_{k.prefix}…
          </div>
          <div role="cell" className="dim" style={{ fontSize: 12 }}>
            {formatDate(k.created_at)}
          </div>
          <div role="cell" className="dim" style={{ fontSize: 12 }}>
            {k.last_used_at ? formatDate(k.last_used_at) : "never"}
          </div>
          <div role="cell">
            {k.revoked_at ? (
              <Tag>Revoked {formatDate(k.revoked_at)}</Tag>
            ) : (
              <Tag>Active</Tag>
            )}
          </div>
          <div role="cell" style={{ textAlign: "right" }}>
            {!k.revoked_at && (
              <button
                type="button"
                className="btn"
                onClick={() => onRevoke(k)}
              >
                Revoke
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateApiKeyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Plaintext key returned by the api; cleared the moment the modal closes. */
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const trimmedName = name.trim();
  const canSubmit =
    !submitting && trimmedName.length > 0 && trimmedName.length <= 80;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.apiKeys.create({ name: trimmedName });
      setPlaintext(r.key);
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async () => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Best-effort; user can still select and copy manually.
    }
  };

  return (
    <ModalShell onClose={onClose} title="Create API key">
      {plaintext === null ? (
        <>
          <p className="dim" style={{ fontSize: 13, marginBottom: 16 }}>
            Pick a name you'll recognize on the keys list (e.g. "macbook claude
            code").
          </p>
          <label
            htmlFor="recspec-new-key-name"
            style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}
          >
            Name
          </label>
          <input
            id="recspec-new-key-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoFocus
            disabled={submitting}
            style={{
              width: "100%",
              marginTop: 4,
              marginBottom: 12,
              padding: "8px 10px",
              border: "1px solid var(--rule)",
              borderRadius: 4,
              fontSize: 14,
              fontFamily: "inherit",
            }}
          />
          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 12,
                padding: "8px 10px",
                background: "var(--paper-2)",
                color: "var(--ink-2)",
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canSubmit}
              onClick={submit}
            >
              {submitting ? "Creating…" : "Create"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div
            role="alert"
            style={{
              marginBottom: 16,
              padding: "12px 14px",
              background: "rgba(255, 200, 80, 0.18)",
              border: "1px solid rgba(255, 160, 50, 0.4)",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <strong>This is the only time you'll see this key.</strong> Copy it
            now — once you dismiss this dialog the plaintext is unrecoverable
            and you'll need to create a new one.
          </div>
          <label
            htmlFor="recspec-new-key-plaintext"
            style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}
          >
            Your new API key
          </label>
          <textarea
            id="recspec-new-key-plaintext"
            readOnly
            value={plaintext}
            rows={2}
            onFocus={(e) => e.currentTarget.select()}
            style={{
              width: "100%",
              marginTop: 4,
              marginBottom: 12,
              padding: "8px 10px",
              border: "1px solid var(--rule)",
              borderRadius: 4,
              background: "var(--paper-2)",
              fontFamily: "var(--font-mono, monospace)",
              fontSize: 13,
              resize: "none",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <button
              type="button"
              className="btn"
              onClick={copyToClipboard}
              aria-live="polite"
            >
              <Icon name="copy" size={14} /> {copied ? "Copied!" : "Copy"}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                // Drop the plaintext from React state on dismissal — there's
                // no way for the user (or anything else) to retrieve it
                // after this point.
                setPlaintext(null);
                onCreated();
              }}
            >
              Done
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function RevokeApiKeyModal({
  target,
  onClose,
  onRevoked,
}: {
  target: ApiKeySummary;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canRevoke = confirm === target.name && !submitting;

  const submit = async () => {
    if (!canRevoke) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.apiKeys.revoke(target.key_id);
      onRevoked();
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError(String(e));
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Revoke API key">
      <p style={{ fontSize: 13, marginBottom: 12 }}>
        Revoking <strong>{target.name}</strong> is irreversible. Any client
        currently using this key will start getting 401 errors immediately.
      </p>
      <label
        htmlFor="recspec-revoke-confirm"
        style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}
      >
        Type the key's name to confirm
      </label>
      <input
        id="recspec-revoke-confirm"
        type="text"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        autoFocus
        disabled={submitting}
        placeholder={target.name}
        style={{
          width: "100%",
          marginTop: 4,
          marginBottom: 12,
          padding: "8px 10px",
          border: "1px solid var(--rule)",
          borderRadius: 4,
          fontSize: 14,
          fontFamily: "inherit",
        }}
      />
      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: "8px 10px",
            background: "var(--paper-2)",
            color: "var(--ink-2)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canRevoke}
          onClick={submit}
        >
          {submitting ? "Revoking…" : "Revoke"}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--paper)",
          padding: "20px 24px",
          borderRadius: 6,
          boxShadow: "var(--sh-3, 0 12px 32px rgba(0,0,0,0.3))",
          minWidth: 420,
          maxWidth: 520,
        }}
      >
        <h2
          className="serif"
          style={{ margin: 0, marginBottom: 16, fontSize: 20, fontWeight: 500 }}
        >
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function DefaultsSection() {
  // Backend endpoint /v1/me/preferences ships in §7. Until then, render
  // the form bound to localStorage values (existing recspec.lang, recspec.tone)
  // so users see something useful and the wiring is in place.
  return (
    <>
      <SectionHeader
        title="Meeting defaults"
        subtitle="Pre-fill the picker with these values for every new meeting."
      />
      <div
        style={{
          padding: 12,
          borderRadius: 8,
          background: "var(--paper-2)",
          fontSize: 13,
          color: "var(--ink-2)",
          marginBottom: 20,
        }}
      >
        <strong>Backend pending.</strong> Persistent preferences ship in
        <code className="mono"> add-production-shell-screens §7</code>. For now
        the picker reads its defaults from localStorage on this device.
      </div>
      <Row label="Language">
        <span className="mono">{localStorage.getItem("recspec.lang") ?? "EN"}</span>
      </Row>
      <Row label="Tone">
        <span style={{ textTransform: "capitalize" }}>
          {localStorage.getItem("recspec.tone") ?? "advisor"}
        </span>
      </Row>
      <Row label="Voice">
        <span style={{ textTransform: "capitalize" }}>highlights</span>
      </Row>
      <Row label="Q&A panel">
        <span>On</span>
      </Row>
      <Row label="Frame capture">
        <span>On</span>
      </Row>
      <div style={{ marginTop: 24 }}>
        <button
          type="button"
          className="btn primary"
          disabled
          title="Backend pending — shipping in §7 of this change."
          style={{ opacity: 0.55, cursor: "not-allowed" }}
        >
          Save defaults
        </button>
      </div>
    </>
  );
}

function BillingSection() {
  return (
    <>
      <SectionHeader
        title="Billing & plan"
        subtitle="Public alpha — no card on file; usage isn't billed yet."
      />
      <Row label="Plan">
        <Tag>Public alpha</Tag>
      </Row>
      <Row label="Meetings this month">
        <span className="mono">— / unlimited</span>
      </Row>
      <Row label="Manage plan">
        <button
          type="button"
          className="btn"
          disabled
          title="Billing portal isn't live yet."
          style={{ opacity: 0.55, cursor: "not-allowed" }}
        >
          Open billing portal
        </button>
      </Row>
    </>
  );
}
